import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import SrtEditorModal from './audio/SrtEditorModal';
import PageHeader from './ui/PageHeader';
import PremiumFeatureBlock from './ui/PremiumFeatureBlock';
import DarkTooltip from './ui/DarkTooltip';
import { useAdmin } from '../contexts/AdminContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Captions,
  AudioLines,
  Download,
  FileText,
  Type,
  Clock,
  RefreshCw,
  Settings2,
  Save,
  Trash2,
  CheckCircle,
  XCircle,
  FileUp,
  Sparkles,
  Eye,
  Pencil,
} from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api/transcricao`;

// Limites espelhados do backend (transcription_service.py) — validação antecipada.
const MAX_UPLOAD_BYTES = 80 * 1024 * 1024;
const SINGLE_CALL_LIMIT = 2 * 1024 * 1024; // acima disso o Worker exige MP3 (chunking)
const MAX_DURATION_SEC = 60 * 60;
const MAX_POLL_TICKS = 180; // ~12 min de polling (4s por tick), depois desiste
const ALLOWED_EXT = ['mp3', 'wav', 'm4a', 'mp4', 'ogg', 'webm', 'mpga', 'mpeg', 'flac'];

// Instância SEM interceptors do app: o PUT presigned no R2 não pode levar o
// header Authorization (conflita com a assinatura na query string).
const r2axios = axios.create();

// Caches leves por sessão (nível de módulo): o histórico renderiza instantâneo
// ao voltar pra página, e reabrir um resultado não refaz o fetch do preview.
let _historyCache = null;
const _previewCache = new Map();

// Botões de download — MESMO estilo dos do resultado do TTS (AudioGeneratorPage:
// DOWNLOAD_BTN/SEG_BTN), incluindo o hover azul.
const DOWNLOAD_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  padding: '6px 12px', borderRadius: '8px',
  fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)',
  background: 'rgba(13,174,255,0.10)', color: '#0DAEFF',
  border: '1px solid rgba(13,174,255,0.22)',
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
};
const SEG_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '6px 10px', fontSize: '10.5px', fontWeight: 500, fontFamily: 'var(--dp-font)',
  background: 'transparent', color: 'var(--dp-text-secondary)', border: 'none',
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s, color 0.15s',
};

const fileExt = (name) => (name && name.includes('.') ? name.split('.').pop().toLowerCase() : '');
const fmtSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`);
const fmtDur = (sec) => {
  if (!sec && sec !== 0) return '--:--';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
};

/** Duração do arquivo via metadata local (sem upload). */
const readDuration = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    const done = (val) => { URL.revokeObjectURL(url); resolve(val); };
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? audio.duration : null);
    audio.onerror = () => done(null);
    audio.src = url;
    setTimeout(() => done(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null), 8000);
  } catch {
    resolve(null);
  }
});

const cardStyle = {
  background: 'var(--dp-bg-card)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '14px',
  padding: '18px 20px',
};

/* ── Helpers do painel de legendas — IDÊNTICOS aos do TTS (AudioGeneratorPage:
   _fillBg/_srtMarker/_srtCard), para os controles ficarem iguais nas 2 páginas. ── */
const fillBg = (pct) => {
  const p = Math.max(0, Math.min(100, pct));
  return `linear-gradient(90deg, var(--dp-blue-500) 0%, var(--dp-blue-500) ${p}%, var(--dp-border-medium) ${p}%, var(--dp-border-medium) 100%)`;
};

const srtMarker = (label, color) => (
  <span style={{
    width: '22px', height: '22px', borderRadius: '7px', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: `${color}1A`, color, fontSize: '11px', fontWeight: 700,
  }}>{label}</span>
);

const srtCard = (marker, title, valueLabel, desc, control, opts = {}) => (
  <div style={{
    background: opts.highlight ? 'rgba(13,174,255,0.06)' : 'var(--dp-bg-card-hover)',
    border: `1px solid ${opts.highlight ? 'rgba(13,174,255,0.22)' : 'var(--dp-border-subtle)'}`,
    borderRadius: '12px', padding: '12px 13px', marginBottom: '9px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      {marker}
      <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>{title}</span>
      {opts.badge && (
        <span style={{
          fontSize: '8.5px', fontWeight: 700, color: '#fff', letterSpacing: '0.5px',
          background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', padding: '2px 7px', borderRadius: '9999px',
        }}>{opts.badge}</span>
      )}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--dp-blue-500)', whiteSpace: 'nowrap' }}>{valueLabel}</span>
    </div>
    <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', lineHeight: 1.45, marginBottom: '9px', fontFamily: 'var(--dp-font)' }}>{desc}</div>
    {control}
  </div>
);

const TranscricaoPage = ({ onLogout }) => {
  usePageTitle('Transcrição');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionData, isLoading, isSuperAdmin } = useAdmin();
  const { sidebarWidth, isMobile } = useSidebar();

  // Espelha o gate do backend (require_paid): assinatura ativa E plano não-free.
  const planTypeEffective = subscriptionData?.plan_type?.toLowerCase() || '';
  const hasAccess = isSuperAdmin
    || (subscriptionData?.has_subscription === true
        && planTypeEffective !== '' && planTypeEffective !== 'free');

  const [file, setFile] = useState(null);
  const [fileDuration, setFileDuration] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | uploading | processing
  const [uploadPct, setUploadPct] = useState(0);
  const [currentJob, setCurrentJob] = useState(null);
  const [preview, setPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // Qual arquivo está no preview: legenda | palavra | tempo | txt.
  const [previewKind, setPreviewKind] = useState('legenda');
  const previewKindRef = useRef('legenda'); // evita closure velha no polling
  const previewReqRef = useRef(0); // descarta resposta atrasada ao trocar rápido
  // Editor de SRT Tempo — MESMO modal do gerador de áudio (words_time.json
  // gravado pelo backend junto dos SRTs). Sem audioUrl: o áudio de origem é
  // apagado na conclusão, então o editor abre sem player.
  const [srtEditorOpen, setSrtEditorOpen] = useState(false);
  const [srtEditorItem, setSrtEditorItem] = useState(null);
  const [historyItems, setHistoryItems] = useState(_historyCache || []);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Mesmos controles/semântica/predefinições do TTS. Idioma NÃO tem seletor:
  // o Whisper detecta automaticamente (o backend já trata language ausente).
  // Padrão: legenda em até 5 palavras (pedido do owner).
  const [settings, setSettings] = useState({
    words: 5, case: 'normal', veoWords: 5, veoMin: 3, veoMax: 8,
  });
  // Config resolvida (canal > usuário > sistema) — guardada para o "Salvar como
  // padrão" devolver voz/velocidade/volume intactos (a predefinição é a MESMA
  // do TTS; salvar daqui não pode apagar o que o usuário definiu lá).
  const audioCfgRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const resultsRef = useRef(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // ── Histórico (7 dias) — cacheado em módulo p/ render instantâneo ──
  const fetchHistory = useCallback(async () => {
    if (!hasAccess) return [];
    try {
      const res = await axios.get(`${API}/history`, authHeaders());
      const items = res.data?.items || [];
      _historyCache = items;
      setHistoryItems(items);
      return items;
    } catch { return []; }
  }, [hasAccess]);

  // ── Predefinições — MESMA base do TTS (canal > usuário > sistema) ──
  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/audio-config/resolve`, authHeaders());
        const cfg = res.data?.config || {};
        audioCfgRef.current = cfg;
        setSettings((s) => ({
          ...s,
          words: cfg.subtitle_words != null ? cfg.subtitle_words : s.words,
          case: cfg.subtitle_case || s.case,
          veoWords: cfg.subtitle_veo_words != null ? cfg.subtitle_veo_words : s.veoWords,
          veoMin: cfg.subtitle_veo_min != null ? cfg.subtitle_veo_min : s.veoMin,
          veoMax: cfg.subtitle_veo_max != null ? cfg.subtitle_veo_max : s.veoMax,
        }));
      } catch { /* mantém os padrões locais */ }
    })();
  }, [hasAccess]);

  // Salva como padrão do usuário (mesmo endpoint do TTS). Devolve voz/velocidade/
  // volume resolvidos intactos: só os campos de legenda mudam a partir daqui.
  const saveAsDefault = async () => {
    setSavingPrefs(true);
    try {
      const cfg = audioCfgRef.current || {};
      await axios.put(`${BACKEND_URL}/api/audio-preferences`, {
        voice_id: cfg.voice_id || null,
        voice_name: cfg.voice_name || null,
        speed: cfg.speed != null ? cfg.speed : 1,
        volume: cfg.volume != null ? cfg.volume : 1,
        subtitle_enabled: cfg.subtitle_enabled != null ? cfg.subtitle_enabled : true,
        subtitle_words: settings.words,
        subtitle_case: settings.case,
        subtitle_veo_words: settings.veoWords,
        subtitle_veo_min: settings.veoMin,
        subtitle_veo_max: settings.veoMax,
      }, authHeaders());
      audioCfgRef.current = {
        ...cfg,
        subtitle_words: settings.words, subtitle_case: settings.case,
        subtitle_veo_words: settings.veoWords,
        subtitle_veo_min: settings.veoMin, subtitle_veo_max: settings.veoMax,
      };
      toast.success(t('audio.prefs_saved', { defaultValue: 'Preferências salvas como padrão' }));
    } catch {
      toast.error(t('transcricao.err_generic'));
    } finally {
      setSavingPrefs(false);
    }
  };

  // Mount: carrega o histórico e RETOMA o polling se há job em andamento
  // (usuário navegou pra fora e voltou durante o processamento).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const items = await fetchHistory();
      if (cancelled) return;
      const processing = items.find((i) => i.status === 'processing');
      if (processing) {
        setPhase('processing');
        startPolling(processing.job_id);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchHistory]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Seleção de arquivo ──────────────────────────────────────────
  const handleFile = async (f) => {
    if (!f || phase !== 'idle') return;
    const ext = fileExt(f.name);
    if (!ALLOWED_EXT.includes(ext)) { toast.error(t('transcricao.err_format')); return; }
    if (f.size > MAX_UPLOAD_BYTES) { toast.error(t('transcricao.err_size')); return; }
    if (f.size > SINGLE_CALL_LIMIT && !['mp3', 'mpga', 'mpeg'].includes(ext)) {
      toast.error(t('transcricao.err_mp3'));
      return;
    }
    const duration = await readDuration(f);
    if (duration && duration > MAX_DURATION_SEC + 5) {
      toast.error(t('transcricao.err_duration'));
      return;
    }
    setFile(f);
    setFileDuration(duration);
    setCurrentJob(null);
    setPreview('');
  };

  // Erros do backend chegam com códigos: traduz pro idioma da UI.
  const jobErrorMessage = (job) => ({
    audio_too_long: t('transcricao.err_duration'),
    no_speech: t('transcricao.err_no_speech'),
    format_requires_mp3: t('transcricao.err_mp3'),
    too_large: t('transcricao.err_size'),
  }[job?.error] || t('transcricao.err_generic'));

  // ── Fluxo principal: presign → PUT direto no R2 → criar job → polling ──
  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_POLL_TICKS) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPhase('idle');
        toast.error(t('transcricao.err_generic'));
        fetchHistory();
        return;
      }
      try {
        const res = await axios.get(`${API}/jobs/${jobId}`, authHeaders());
        const job = res.data;
        if (job.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setCurrentJob(job);
          setPhase('idle');
          setFile(null);
          setFileDuration(null);
          toast.success(t('transcricao.toast_done'));
          fetchHistory();
          loadPreview(job);
        } else if (job.status === 'failed' || job.status === 'expired') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPhase('idle');
          toast.error(jobErrorMessage(job));
          fetchHistory();
        }
      } catch { /* tenta de novo no próximo tick */ }
    }, 4000);
  };

  // Busca conteúdo do CDN com fallback no proxy do backend (CORS).
  const fetchCdn = async (url, asBlob = false) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('cdn');
      return asBlob ? await res.blob() : await res.text();
    } catch {
      const res = await axios.post(`${BACKEND_URL}/api/proxy-download`, { url }, {
        ...authHeaders(),
        responseType: asBlob ? 'blob' : 'text',
      });
      return res.data;
    }
  };

  // URL de cada arquivo previsualizável do job, por tipo.
  const previewUrl = (job, kind) => ({
    legenda: job?.srt_url,
    palavra: job?.srt_palavra_url,
    tempo: job?.srt_tempo_url,
    txt: job?.txt_url,
  }[kind]);

  // Preview de QUALQUER um dos 4 arquivos, com cache por job+tipo: reabrir do
  // histórico ou alternar de arquivo é instantâneo (sem refetch) e o container
  // fica FIXO — só o conteúdo interno troca (sem piscar). Sem `kindWanted`,
  // mantém o tipo selecionado (com fallback pro primeiro arquivo existente).
  const loadPreview = async (job, kindWanted) => {
    const wanted = kindWanted || previewKindRef.current;
    const kind = previewUrl(job, wanted)
      ? wanted
      : ['legenda', 'palavra', 'tempo', 'txt'].find((k) => previewUrl(job, k));
    if (!job || !kind) { setPreview(''); return; }
    previewKindRef.current = kind;
    setPreviewKind(kind);
    const cacheKey = `${job.job_id}:${kind}`;
    const reqId = ++previewReqRef.current;
    const cached = _previewCache.get(cacheKey);
    if (cached != null) { setPreview(cached); setPreviewLoading(false); return; }
    setPreviewLoading(true);
    try {
      const content = String(await fetchCdn(previewUrl(job, kind), false)).slice(0, 6000);
      _previewCache.set(cacheKey, content);
      if (previewReqRef.current === reqId) setPreview(content);
    } catch { /* preview é opcional */ }
    finally { if (previewReqRef.current === reqId) setPreviewLoading(false); }
  };

  const handleGenerate = async () => {
    if (!file || phase !== 'idle') return;
    if (settings.veoMin > settings.veoMax) { toast.error(t('transcricao.err_range')); return; }
    setPhase('uploading');
    setUploadPct(0);
    try {
      const presign = await axios.post(`${API}/presign`, {
        filename: file.name,
        size: file.size,
        content_type: file.type || undefined,
      }, authHeaders());
      const { upload_url, key, content_type } = presign.data;

      await r2axios.put(upload_url, file, {
        headers: { 'Content-Type': content_type },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });

      const jobRes = await axios.post(`${API}/jobs`, {
        key,
        filename: file.name,
        // Duração desconhecida (VBR sem header, MediaRecorder): estimativa por
        // tamanho LIMITADA a <1h — a verificação real é pós-transcrição, com
        // estorno; estimar acima causaria 413 falso em MP3 de bitrate alto.
        duration_sec: fileDuration || Math.min(3500, Math.max(1, Math.round(file.size / (16 * 1024)))),
        subtitle_words: settings.words,
        subtitle_case: settings.case,
        veo_words: settings.veoWords,
        veo_min: settings.veoMin,
        veo_max: settings.veoMax,
      }, authHeaders());

      setPhase('processing');
      startPolling(jobRes.data.job_id);
    } catch (err) {
      setPhase('idle');
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast.error(t('transcricao.err_quota'));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('transcricao.err_upload'));
      }
    }
  };

  const downloadFile = async (url, name) => {
    try {
      const blob = await fetchCdn(url, true);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      window.open(url, '_blank');
    }
  };

  // Regra única (igual ao áudio): TEM nome → "<nome>_<tipo>"; SEM nome →
  // "Darkplanner_<tipo>". Na transcrição o nome é o arquivo enviado, então
  // quase sempre usa "<arquivo>_...". Tipos = nomes dos arquivos no R2.
  const dlBase = (job) => ((job?.filename || '').replace(/\.[^.]+$/, '') || 'Darkplanner');
  const DOWNLOADS = (job) => [
    { url: job.srt_url, name: `${dlBase(job)}_legenda.srt`, label: t('transcricao.dl_legenda'), Icon: Captions },
    { url: job.srt_palavra_url, name: `${dlBase(job)}_srtsyncpalavra.srt`, label: t('transcricao.dl_palavra'), Icon: FileText },
    { url: job.srt_tempo_url, name: `${dlBase(job)}_srtsynctempo.srt`, label: t('transcricao.dl_tempo'), Icon: Clock },
    { url: job.txt_url, name: `${dlBase(job)}_transcricao.txt`, label: t('transcricao.dl_txt'), Icon: FileText },
  ].filter((d) => d.url);

  // Abre o Editor de SRT Tempo do job (mesmo modal/inteligência do gerador de
  // áudio): o modal baixa o srtsynctempo.srt + words_time.json do mesmo prefixo
  // e edita só as quebras — a sincronização real nunca muda.
  const openSrtEditor = (job) => {
    if (!job?.srt_tempo_url) return;
    setSrtEditorItem({
      jobId: job.job_id,
      fileName: job.filename,
      srtTempo: job.srt_tempo_url,
      audioUrl: null,
      createdAt: job.created_at,
    });
    setSrtEditorOpen(true);
  };

  // Reabre um item do histórico no card de Resultados (ficam até 7 dias).
  // O container do preview permanece montado; só o conteúdo interno troca.
  const openFromHistory = (item) => {
    if (item.status !== 'completed') return;
    setCurrentJob(item);
    loadPreview(item);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Exclui uma transcrição do histórico (DB + arquivos no R2). Atualiza a lista
  // na hora (otimista no estado/cache). Se o item aberto for o excluído, limpa
  // o card de resultados. Não estorna cota (o job já foi consumido).
  const deleteHistoryItem = async (item) => {
    if (!item?.job_id || deletingId) return;
    setDeletingId(item.job_id);
    try {
      await axios.delete(`${API}/jobs/${item.job_id}`, authHeaders());
      const next = (historyItems || []).filter((h) => h.job_id !== item.job_id);
      _historyCache = next;
      setHistoryItems(next);
      [..._previewCache.keys()].forEach((k) => {
        if (k.startsWith(`${item.job_id}:`)) _previewCache.delete(k);
      });
      if (currentJob?.job_id === item.job_id) { setCurrentJob(null); setPreview(''); }
      toast.success(t('transcricao.delete_done'));
    } catch {
      toast.error(t('transcricao.err_generic'));
    } finally {
      setDeletingId(null);
    }
  };

  /* ─── Loading / Free ─── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <Header />
        <main className="dp-main-content" style={{ marginLeft: isMobile ? 0 : `${sidebarWidth}px`, marginTop: '60px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="dp-skeleton" style={{ width: '240px', height: '24px' }} />
        </main>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <Header />
        <PremiumFeatureBlock />
      </div>
    );
  }

  const busy = phase !== 'idle';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <Header />
        <main className="dp-main-content" style={{ marginTop: '60px', flex: 1, animation: 'dp-pageFadeIn 0.35s ease' }}>
          <PageHeader
            icon={<Captions size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
            title={t('transcricao.title')}
            description={t('transcricao.subtitle')}
          />

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '360px minmax(0, 1fr)',
            gap: '18px',
            alignItems: 'start',
          }}>
            {/* ═══ COLUNA ESQUERDA: upload + configurações ═══ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Upload */}
              <div className="dp-top-accent" style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <FileUp size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                    {t('transcricao.upload_title')}
                  </span>
                  <span style={{ fontSize: '10.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginLeft: 'auto' }}>
                    {t('transcricao.upload_formats')}
                  </span>
                </div>

                <div
                  onClick={() => !busy && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); if (!busy) setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                  style={{
                    border: `1.5px dashed ${isDragging ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-medium)'}`,
                    borderRadius: '12px',
                    padding: '22px 14px',
                    textAlign: 'center',
                    cursor: busy ? 'default' : 'pointer',
                    background: isDragging ? 'rgba(13,174,255,0.05)' : 'transparent',
                    transition: 'all 0.2s ease',
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.m4a,.mp4,.ogg,.webm,.flac,audio/*"
                    style={{ display: 'none' }}
                    onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                  />
                  <AudioLines size={24} style={{ color: 'var(--dp-blue-500, #0DAEFF)', marginBottom: '8px' }} />
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', marginBottom: '4px' }}>
                    {t('transcricao.upload_cta')}
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
                    {t('transcricao.upload_note')}
                  </div>
                </div>

                {file && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    marginTop: '10px', padding: '10px 12px',
                    background: 'var(--dp-bg-secondary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '10px',
                  }}>
                    <AudioLines size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {file.name}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                        {fmtSize(file.size)} · {fmtDur(fileDuration)}
                      </div>
                    </div>
                    {!busy && (
                      <button
                        onClick={() => { setFile(null); setFileDuration(null); }}
                        title={t('transcricao.remove_file')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--dp-text-tertiary)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Configurações de legenda — MESMO estilo/textos do painel do TTS
                  (AudioGeneratorPage settingsPanel): cards com marcador, slider
                  duplo do VEO·SYNC, sliders com fill azul e formato Aa/AA/aa. */}
              <div className="dp-top-accent" style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <Settings2 size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {t('audio.subtitle_section')}
                  </span>
                  {/* Salvar como padrão — mesma predefinição de legendas do TTS */}
                  <DarkTooltip content={t('transcricao.tip_save')} position="top" maxWidth={240}>
                  <button
                    onClick={saveAsDefault}
                    disabled={savingPrefs}
                    style={{
                      marginLeft: 'auto',
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '4px 10px', borderRadius: '7px',
                      background: 'transparent',
                      border: '1px solid var(--dp-border-medium)',
                      color: 'var(--dp-text-secondary)',
                      fontSize: '10.5px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)', cursor: 'pointer',
                      opacity: savingPrefs ? 0.6 : 1,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.40)'; e.currentTarget.style.color = '#0DAEFF'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                  >
                    <Save size={11} />
                    {savingPrefs ? t('audio.saving') : t('audio.save_default')}
                  </button>
                  </DarkTooltip>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', lineHeight: 1.4, margin: '0 2px 10px', fontFamily: 'var(--dp-font)' }}>
                  {t('audio.srt_intro')}
                </div>

                {/* PRINCIPAL: Por tempo (VEO·SYNC) — slider duplo */}
                {srtCard(
                  srtMarker('⏱', '#0DAEFF'),
                  t('audio.subtitle_tempo'),
                  `${settings.veoMin}–${settings.veoMax}s`,
                  t('audio.srt_desc_tempo'),
                  <div style={{ position: 'relative', height: '16px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: '6px', background: 'var(--dp-border-medium)', borderRadius: '9999px' }} />
                    <div style={{
                      position: 'absolute', height: '6px', borderRadius: '9999px', top: '50%', transform: 'translateY(-50%)',
                      background: 'linear-gradient(90deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                      left: `${((settings.veoMin - 1) / 24) * 100}%`, right: `${100 - ((settings.veoMax - 1) / 24) * 100}%`,
                    }} />
                    <input type="range" min="1" max="25" step="1" value={settings.veoMin}
                      onChange={(e) => setSettings((s) => ({ ...s, veoMin: Math.min(parseInt(e.target.value), s.veoMax) }))}
                      className="ag-range-dual" style={{ zIndex: 3 }} aria-label={`${t('audio.veo_duration')} (min)`} />
                    <input type="range" min="1" max="25" step="1" value={settings.veoMax}
                      onChange={(e) => setSettings((s) => ({ ...s, veoMax: Math.max(parseInt(e.target.value), s.veoMin) }))}
                      className="ag-range-dual" style={{ zIndex: 4 }} aria-label={`${t('audio.veo_duration')} (max)`} />
                  </div>,
                  { highlight: true, badge: t('audio.srt_tempo_badge') },
                )}

                {/* Por frase natural */}
                {srtCard(
                  srtMarker('¶', '#0DAEFF'),
                  t('audio.subtitle_legenda'),
                  `${t('audio.max_label')} ${settings.words}`,
                  t('audio.srt_desc_legenda'),
                  <input type="range" min="1" max="15" step="1" value={settings.words}
                    onChange={(e) => setSettings((s) => ({ ...s, words: parseInt(e.target.value) }))}
                    className="ag-slider" style={{ width: '100%', background: fillBg(((settings.words - 1) / 14) * 100) }} />,
                )}

                {/* Nº fixo de palavras */}
                {srtCard(
                  srtMarker('#', '#0DAEFF'),
                  t('audio.subtitle_palavra'),
                  `${settings.veoWords}`,
                  t('audio.srt_desc_palavra'),
                  <input type="range" min="1" max="15" step="1" value={settings.veoWords}
                    onChange={(e) => setSettings((s) => ({ ...s, veoWords: parseInt(e.target.value) }))}
                    className="ag-slider" style={{ width: '100%', background: fillBg(((settings.veoWords - 1) / 14) * 100) }} />,
                )}

                {/* Maiúsculas — linha compacta Aa / AA / aa */}
                <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', marginLeft: '2px', fontFamily: 'var(--dp-font)' }}>{t('audio.format')}</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[
                      { id: 'normal', label: 'Aa', tip: t('audio.format_tip_normal') },
                      { id: 'upper', label: 'AA', tip: t('audio.format_tip_upper') },
                      { id: 'lower', label: 'aa', tip: t('audio.format_tip_lower') },
                    ].map((opt) => (
                      <DarkTooltip key={opt.id} content={opt.tip} position="top" maxWidth={220}>
                        <button onClick={() => setSettings((s) => ({ ...s, case: opt.id }))}
                          style={{
                            padding: '4px 11px', textAlign: 'center', borderRadius: '7px',
                            border: settings.case === opt.id ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-subtle)',
                            background: settings.case === opt.id ? 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))' : 'var(--dp-bg-card-hover)',
                            color: settings.case === opt.id ? 'white' : 'var(--dp-text-secondary)',
                            fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.25s',
                          }}>
                          {opt.label}
                        </button>
                      </DarkTooltip>
                    ))}
                  </div>
                </div>
              </div>

              {/* Botão gerar */}
              <button
                className="dp-btn-cta"
                onClick={handleGenerate}
                disabled={!file || busy}
                style={{ width: '100%', justifyContent: 'center', opacity: (!file || busy) ? 0.6 : 1 }}
              >
                {phase === 'uploading'
                  ? t('transcricao.uploading', { pct: uploadPct })
                  : phase === 'processing'
                    ? t('transcricao.processing')
                    : t('transcricao.generate')}
              </button>
            </div>

            {/* ═══ COLUNA DIREITA: resultado + histórico ═══ */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
              <div ref={resultsRef} className="dp-top-accent" style={{ ...cardStyle, minHeight: '260px', display: 'flex', flexDirection: 'column', scrollMarginTop: '80px' }}>
                {/* Header premium: ícone com degradê da marca + selo de retenção */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                    boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <FileText size={17} color="#fff" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      {t('transcricao.results_title')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                      {t('transcricao.history_note')}
                    </div>
                  </div>
                  {currentJob && phase !== 'processing' && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      fontSize: '10.5px', fontWeight: 700, color: '#34D399',
                      background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.22)',
                      padding: '3px 10px', borderRadius: '9999px', fontFamily: 'var(--dp-font)',
                      letterSpacing: '0.4px',
                    }}>
                      <CheckCircle size={11} />
                      {t('transcricao.status_completed').toUpperCase()}
                    </span>
                  )}
                </div>

                {phase === 'processing' && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '30px 0' }}>
                    <RefreshCw size={26} className="animate-spin" style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                    <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', textAlign: 'center', lineHeight: 1.6, maxWidth: '380px' }}>
                      {t('transcricao.status_processing')}
                    </div>
                  </div>
                )}

                {phase !== 'processing' && !currentJob && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '30px 0' }}>
                    <div style={{
                      width: '52px', height: '52px', borderRadius: '14px',
                      background: 'rgba(13,174,255,0.07)', border: '1px solid rgba(13,174,255,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px',
                    }}>
                      <Captions size={24} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                      {t('transcricao.empty_title')}
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                      {t('transcricao.empty_desc')}
                    </div>
                  </div>
                )}

                {phase !== 'processing' && currentJob && (
                  <div>
                    {/* Arquivo + chips de metadados */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                      marginBottom: '14px', padding: '11px 14px',
                      background: 'rgba(13,174,255,0.05)',
                      border: '1px solid rgba(13,174,255,0.12)',
                      borderRadius: '11px',
                    }}>
                      <AudioLines size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: '140px', fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', wordBreak: 'break-all' }}>
                        {currentJob.filename}
                      </span>
                      {[
                        currentJob.duration_sec ? fmtDur(currentJob.duration_sec) : null,
                        currentJob.language ? String(currentJob.language).toUpperCase() : null,
                        currentJob.text_chars ? `${Number(currentJob.text_chars).toLocaleString('pt-BR')} chars` : null,
                      ].filter(Boolean).map((chip) => (
                        <span key={chip} style={{
                          fontSize: '10.5px', fontWeight: 600, color: 'var(--dp-text-secondary)',
                          background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
                          padding: '3px 9px', borderRadius: '9999px', fontFamily: 'var(--dp-font)',
                          whiteSpace: 'nowrap',
                        }}>
                          {chip}
                        </span>
                      ))}
                    </div>

                    {/* Pré-visualização — MESMO visual dos botões do TTS, mas aqui
                        eles TROCAM o arquivo mostrado no preview abaixo. O download
                        de cada arquivo fica nos botões do histórico. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                      {currentJob.txt_url && (() => {
                        const active = previewKind === 'txt';
                        return (
                          <DarkTooltip content={`${t('transcricao.tip_txt')} ${t('transcricao.tip_preview')}`} maxWidth={260} delay={300}>
                            <button
                              onClick={() => loadPreview(currentJob, 'txt')}
                              style={{
                                ...DOWNLOAD_BTN,
                                background: active ? 'rgba(13,174,255,0.22)' : 'rgba(13,174,255,0.10)',
                                borderColor: active ? 'rgba(13,174,255,0.50)' : 'rgba(13,174,255,0.22)',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.22)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.50)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = active ? 'rgba(13,174,255,0.22)' : 'rgba(13,174,255,0.10)'; e.currentTarget.style.borderColor = active ? 'rgba(13,174,255,0.50)' : 'rgba(13,174,255,0.22)'; }}
                            >
                              <Eye size={12} /> {t('transcricao.dl_txt')}
                            </button>
                          </DarkTooltip>
                        );
                      })()}
                      {(currentJob.srt_url || currentJob.srt_palavra_url || currentJob.srt_tempo_url) && (() => {
                        const srtOpts = [
                          currentJob.srt_url && { key: 'legenda', tip: t('audio.tip_legenda'), label: t('audio.dl_legenda'), icon: <FileText size={11} /> },
                          currentJob.srt_palavra_url && { key: 'palavra', tip: t('audio.tip_palavra'), label: t('audio.dl_palavra'), icon: <Type size={11} /> },
                          currentJob.srt_tempo_url && { key: 'tempo', tip: t('audio.tip_tempo'), label: t('audio.dl_tempo'), icon: <Clock size={11} /> },
                        ].filter(Boolean);
                        return (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.4px', color: 'var(--dp-text-tertiary)' }}>SRT</span>
                            <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', overflow: 'hidden' }}>
                              {srtOpts.map((o, i) => {
                                const active = previewKind === o.key;
                                return (
                                  <DarkTooltip key={o.key} content={`${o.tip} ${t('transcricao.tip_preview')}`} maxWidth={260} delay={300}>
                                    <button
                                      onClick={() => loadPreview(currentJob, o.key)}
                                      style={{
                                        ...SEG_BTN,
                                        borderLeft: i > 0 ? '1px solid var(--dp-border-subtle)' : 'none',
                                        background: active ? 'rgba(13,174,255,0.12)' : 'transparent',
                                        color: active ? '#0DAEFF' : 'var(--dp-text-secondary)',
                                        fontWeight: active ? 600 : 500,
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.color = '#0DAEFF'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = active ? 'rgba(13,174,255,0.12)' : 'transparent'; e.currentTarget.style.color = active ? '#0DAEFF' : 'var(--dp-text-secondary)'; }}
                                    >
                                      {o.icon} {o.label}
                                    </button>
                                  </DarkTooltip>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Editar SRT Tempo — mesmo editor do gerador de áudio */}
                      {currentJob.srt_tempo_url && (
                        <DarkTooltip content={t('audio.srt_edit_tip')} maxWidth={260} delay={300}>
                          <button
                            onClick={() => openSrtEditor(currentJob)}
                            style={{ ...SEG_BTN, border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', color: '#0DAEFF' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.35)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                          >
                            <Pencil size={11} /> {t('audio.srt_edit_btn')}
                          </button>
                        </DarkTooltip>
                      )}
                      {/* Ponte com a Biblioteca de Skills: o SRT gerado aqui é a
                          entrada das skills principais (SRT + estilo visual). */}
                      {(currentJob.srt_url || currentJob.srt_palavra_url || currentJob.srt_tempo_url) && (
                        <DarkTooltip content={t('skills.use_with_skill_tip')} maxWidth={280} delay={300}>
                          <button
                            onClick={() => navigate('/prompts')}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '6px 11px', borderRadius: '8px',
                              fontSize: '11.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
                              cursor: 'pointer', transition: 'all 0.2s ease', lineHeight: 1.4,
                              border: '1px dashed rgba(13,174,255,0.35)',
                              background: 'transparent',
                              color: 'var(--dp-blue-500, #0DAEFF)',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.08)'; e.currentTarget.style.borderStyle = 'solid'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderStyle = 'dashed'; }}
                          >
                            <Sparkles size={12} /> {t('skills.use_with_skill')}
                          </button>
                        </DarkTooltip>
                      )}
                    </div>

                    {/* Preview FIXO: o container nunca desmonta ao trocar de
                        resultado — só o conteúdo interno muda, com fade (sem
                        piscar e sem pular o layout). Cache por job = troca
                        instantânea ao reabrir do histórico. */}
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                        {t('transcricao.preview_title')}
                        <span style={{ color: 'var(--dp-blue-500, #0DAEFF)' }}>
                          {' · '}
                          {{
                            legenda: t('audio.dl_legenda'),
                            palavra: t('audio.dl_palavra'),
                            tempo: t('audio.dl_tempo'),
                            txt: t('transcricao.dl_txt'),
                          }[previewKind]}
                        </span>
                      </div>
                      <div style={{
                        height: '300px',
                        background: 'var(--dp-bg-secondary)',
                        border: '1px solid var(--dp-border-subtle)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                      }}>
                        {previewLoading ? (
                          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {[88, 64, 76, 58, 82, 70, 50].map((w, i) => (
                              <div key={i} className="dp-skeleton" style={{ height: '11px', width: `${w}%`, borderRadius: '5px' }} />
                            ))}
                          </div>
                        ) : preview ? (
                          <pre key={`${currentJob.job_id}:${previewKind}`} style={{
                            margin: 0, padding: '12px 14px',
                            height: '100%', boxSizing: 'border-box',
                            fontSize: '12px', lineHeight: 1.6,
                            color: 'var(--dp-text-secondary)',
                            fontFamily: 'monospace',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            animation: 'dp-pageFadeIn 0.25s ease',
                          }}>
                            {preview}
                          </pre>
                        ) : (
                          <div style={{
                            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                          }}>
                            {t('transcricao.preview_empty')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Histórico 24h */}
              <div className="dp-top-accent" style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Clock size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                    {t('transcricao.history_title')}
                  </span>
                  <span style={{ fontSize: '10.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginLeft: 'auto' }}>
                    {t('transcricao.history_note')}
                  </span>
                </div>

                {historyItems.length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', padding: '14px 0 8px' }}>
                    {t('transcricao.history_empty')}
                  </div>
                ) : historyItems.map((item) => {
                  const statusCfg = {
                    completed: { label: t('transcricao.status_completed'), color: '#34D399', Icon: CheckCircle },
                    processing: { label: t('transcricao.status_processing_short'), color: 'var(--dp-blue-500, #0DAEFF)', Icon: RefreshCw },
                    failed: { label: t('transcricao.status_failed'), color: '#F87171', Icon: XCircle },
                    expired: { label: t('transcricao.status_expired'), color: 'var(--dp-text-tertiary)', Icon: Clock },
                  }[item.status] || { label: item.status, color: 'var(--dp-text-tertiary)', Icon: Clock };
                  const clickable = item.status === 'completed';
                  return (
                    <div
                      key={item.job_id}
                      onClick={() => openFromHistory(item)}
                      className={clickable ? 'tr-hist-row' : ''}
                      title={clickable ? t('transcricao.history_open') : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                        padding: '10px 8px',
                        borderTop: '1px solid var(--dp-border-subtle)',
                        marginTop: '8px', borderRadius: '9px',
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'background 0.15s ease',
                      }}
                    >
                      <statusCfg.Icon size={14} style={{ color: statusCfg.color, flexShrink: 0 }} className={item.status === 'processing' ? 'animate-spin' : ''} />
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', wordBreak: 'break-all' }}>
                          {item.filename}
                        </div>
                        <div style={{ fontSize: '11px', color: statusCfg.color, fontFamily: 'var(--dp-font)' }}>
                          {statusCfg.label}{item.duration_sec ? ` · ${fmtDur(item.duration_sec)}` : ''}
                        </div>
                      </div>
                      {item.status !== 'processing' && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          {item.status === 'completed' && DOWNLOADS(item).map((d) => (
                            <button
                              key={d.name}
                              onClick={() => downloadFile(d.url, d.name)}
                              title={d.label}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '5px 10px', borderRadius: '7px',
                                background: 'transparent',
                                border: '1px solid var(--dp-border-medium)',
                                color: 'var(--dp-text-secondary)',
                                fontSize: '11px', fontWeight: 500,
                                fontFamily: 'var(--dp-font)', cursor: 'pointer',
                                transition: 'all 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.color = '#0DAEFF'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.40)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                            >
                              <Download size={11} />
                              {d.label}
                            </button>
                          ))}
                          {/* Excluir do histórico — apaga DB + arquivos no R2 */}
                          <DarkTooltip content={t('transcricao.delete')} position="top">
                            <button
                              onClick={() => deleteHistoryItem(item)}
                              disabled={deletingId === item.job_id}
                              title={t('transcricao.delete')}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '5px', borderRadius: '7px',
                                background: 'transparent', border: '1px solid var(--dp-border-medium)',
                                color: 'var(--dp-text-tertiary)', cursor: deletingId === item.job_id ? 'not-allowed' : 'pointer',
                                opacity: deletingId === item.job_id ? 0.5 : 1, transition: 'all 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#F87171'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.40)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                            >
                              {deletingId === item.job_id ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            </button>
                          </DarkTooltip>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sliders idênticos aos do TTS (.ag-slider/.ag-range-dual copiados do
              AudioGeneratorPage) + tiles de download e hover do histórico. */}
          <style>{`
            .ag-slider {
              -webkit-appearance: none;
              width: 100%;
              height: 6px;
              background: var(--dp-border-medium);
              border-radius: 9999px;
              outline: none;
              cursor: ew-resize;
            }
            .ag-slider::-webkit-slider-thumb {
              -webkit-appearance: none;
              width: 5px;
              height: 16px;
              border-radius: 3px;
              background: #ffffff;
              cursor: ew-resize;
              box-shadow: 0 1px 4px rgba(0,0,0,0.45);
              transition: transform 0.12s ease;
            }
            .ag-slider::-webkit-slider-thumb:hover { transform: scaleY(1.15); }
            .ag-slider::-moz-range-thumb {
              width: 5px;
              height: 16px;
              border-radius: 3px;
              background: #ffffff;
              cursor: ew-resize;
              border: none;
              box-shadow: 0 1px 4px rgba(0,0,0,0.45);
            }
            .ag-range-dual {
              -webkit-appearance: none;
              appearance: none;
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: 16px;
              margin: 0;
              background: transparent;
              pointer-events: none;
              outline: none;
            }
            .ag-range-dual::-webkit-slider-runnable-track { background: transparent; border: none; }
            .ag-range-dual::-moz-range-track { background: transparent; border: none; }
            .ag-range-dual::-webkit-slider-thumb {
              -webkit-appearance: none;
              pointer-events: auto;
              width: 5px;
              height: 16px;
              border-radius: 3px;
              background: #ffffff;
              cursor: ew-resize;
              box-shadow: 0 1px 4px rgba(0,0,0,0.45);
              transition: transform 0.12s ease;
            }
            .ag-range-dual::-webkit-slider-thumb:hover { transform: scaleY(1.15); }
            .ag-range-dual::-moz-range-thumb {
              pointer-events: auto;
              width: 5px;
              height: 16px;
              border-radius: 3px;
              background: #ffffff;
              cursor: ew-resize;
              border: none;
              box-shadow: 0 1px 4px rgba(0,0,0,0.45);
            }
            .tr-hist-row:hover { background: rgba(255,255,255,0.03); }
          `}</style>
        </main>
      </div>

      {/* Editor de SRT Tempo — mesmo modal do gerador de áudio; resultados da
          transcrição ficam 7 dias no R2 (vs 24h do TTS). */}
      <SrtEditorModal
        open={srtEditorOpen}
        onOpenChange={setSrtEditorOpen}
        item={srtEditorItem}
        expiryHours={7 * 24}
      />
    </div>
  );
};

export default TranscricaoPage;
